import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { formatPrice } from '../../lib/cart';
import { loadOnlineSales, type OnlineSalesSummary } from '../../lib/stripeStats';
import { loadStripePayouts, type StripePayoutsInfo } from '../../lib/stripePayouts';

/**
 * Section Paiements Stripe — portée du back-office legacy.
 * Lit le statut Stripe Connect du tenant (colonnes `stripe_*`) et lance l'onboarding
 * via l'edge function `stripe-onboard` (même projet Supabase). Encaissement direct sur
 * le compte du restaurateur ; Lil & Co prélève 0,99 par commande.
 */
interface StripeTenant {
  slug?: string;
  stripe_account_id?: string | null;
  stripe_charges_enabled?: boolean | null;
  stripe_details_submitted?: boolean | null;
  stripe_requirements_disabled_reason?: string | null;
}

export default function StripeSection({
  tenantId,
  currency = 'EUR',
}: {
  tenantId: string;
  currency?: 'EUR' | 'CHF';
}) {
  const [t, setT] = useState<StripeTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [sales, setSales] = useState<OnlineSalesSummary | null>(null);
  const [payouts, setPayouts] = useState<StripePayoutsInfo | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from('tenants').select('*').eq('id', tenantId).single();
    const tt = (data as StripeTenant) ?? null;
    setT(tt);
    setLoading(false);
    // Ventes + versements seulement si l'encaissement est actif.
    if (tt?.stripe_charges_enabled) {
      loadOnlineSales(tenantId).then(setSales).catch(() => {});
      loadStripePayouts(tenantId).then(setPayouts).catch(() => {});
    }
  }, [tenantId]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboard')) {
      setMsg(
        params.get('onboard') === 'success'
          ? 'Onboarding terminé. Stripe vérifie votre dossier (1-2 min).'
          : 'Onboarding interrompu. Vous pouvez le reprendre.',
      );
    }
  }, [load]);

  const startOnboarding = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const returnBase = window.location.origin + window.location.pathname;
      const { data, error } = await supabase.functions.invoke('stripe-onboard', {
        body: {
          tenant_id: tenantId,
          tenant_slug: t?.slug,
          return_url: returnBase + '?onboard=success',
          refresh_url: returnBase + '?onboard=refresh',
        },
      });
      if (error) {
        setMsg('Erreur Stripe : ' + (error.message || 'voir console'));
      } else if (data?.url) {
        window.location.href = data.url;
      } else if (data?.error) {
        setMsg('Erreur : ' + data.error);
      } else {
        setMsg('Réponse vide de stripe-onboard.');
      }
    } catch (e) {
      setMsg('Erreur : ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="opacity-70">Chargement…</p>;

  const hasAccount = !!t?.stripe_account_id;
  const isActive = !!t?.stripe_charges_enabled;
  const detailsSubmitted = !!t?.stripe_details_submitted;
  const fee = currency === 'CHF' ? '0,99 CHF' : '0,99 €';

  const Row = ({ label, ok }: { label: string; ok: boolean }) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={'font-bold ' + (ok ? 'text-emerald-600' : 'text-slate-400')}>
        {ok ? '✅ Oui' : '⏳ Non'}
      </span>
    </div>
  );

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {msg && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">{msg}</div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100 text-2xl">💳</div>
          <div>
            <h2 className="text-2xl font-extrabold">Paiements en ligne par Stripe</h2>
            <p className="text-sm text-slate-500">
              Encaissez vos commandes en ligne directement sur votre compte bancaire.
            </p>
          </div>
        </div>

        <div className="mb-4 space-y-2 rounded-xl bg-slate-50 p-4">
          <Row label="Compte Stripe créé :" ok={hasAccount} />
          <Row label="Onboarding KYC complété :" ok={detailsSubmitted} />
          <Row label="Encaissement actif :" ok={isActive} />
          {t?.stripe_requirements_disabled_reason && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
              ⚠ Stripe demande : {t.stripe_requirements_disabled_reason}
            </div>
          )}
        </div>

        {!isActive && (
          <button
            onClick={startOnboarding}
            disabled={busy}
            className="w-full rounded-xl bg-purple-600 py-4 font-extrabold text-white shadow-lg transition hover:bg-purple-700 disabled:opacity-50"
          >
            {busy
              ? 'Connexion à Stripe…'
              : hasAccount
                ? "🔄 Continuer l'onboarding Stripe"
                : '🚀 Activer les paiements en ligne'}
          </button>
        )}

        {isActive && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
            <div className="flex items-center gap-2 font-extrabold">✅ Paiements en ligne activés</div>
            <div className="mt-1 text-sm">
              Les commandes payées en ligne arrivent sur votre compte bancaire (virement Stripe J+2 ouvrés).
              Lil &amp; Co prélève {fee} par commande, quel que soit le montant.
            </div>
          </div>
        )}

        <button onClick={load} className="mt-3 text-xs font-semibold text-slate-500 hover:text-slate-700">
          🔄 Rafraîchir le statut
        </button>
      </div>

      {isActive && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-base font-extrabold">📈 Ventes en ligne (CB Stripe)</h3>
          {!sales ? (
            <p className="text-sm opacity-60">Chargement…</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                {([
                  ['Aujourd’hui', sales.today],
                  ['7 jours', sales.last7],
                  ['30 jours', sales.last30],
                ] as const).map(([label, b]) => (
                  <div key={label} className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs opacity-60">{label}</div>
                    <div className="text-lg font-extrabold">{formatPrice(b.cents, currency)}</div>
                    <div className="text-xs opacity-60">{b.count} cmd</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-sm text-slate-600">
                Ticket moyen (30 j) : <b>{formatPrice(sales.avgTicketCents, currency)}</b>
              </div>
            </>
          )}
        </div>
      )}

      {isActive && payouts && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-base font-extrabold">🏦 Versements Stripe</h3>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-xl bg-emerald-50 p-3">
              <div className="text-xs opacity-60">Solde disponible</div>
              <div className="text-lg font-extrabold text-emerald-700">
                {formatPrice(payouts.availableCents, payouts.currency as 'EUR' | 'CHF')}
              </div>
            </div>
            <div className="rounded-xl bg-amber-50 p-3">
              <div className="text-xs opacity-60">En attente</div>
              <div className="text-lg font-extrabold text-amber-700">
                {formatPrice(payouts.pendingCents, payouts.currency as 'EUR' | 'CHF')}
              </div>
            </div>
          </div>
          {payouts.payouts.length > 0 ? (
            <ul className="mt-3 divide-y text-sm">
              {payouts.payouts.map((p, i) => (
                <li key={i} className="flex items-center justify-between py-1.5">
                  <span className="text-slate-600">
                    {new Date(p.arrival_date * 1000).toLocaleDateString('fr-FR')} · {p.status}
                  </span>
                  <span className="font-semibold">{formatPrice(p.amount_cents, p.currency.toUpperCase() as 'EUR' | 'CHF')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm opacity-60">Aucun versement récent.</p>
          )}
        </div>
      )}

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-6 text-sm">
        <h3 className="font-extrabold">Comment ça marche</h3>
        <ol className="list-inside list-decimal space-y-1 text-slate-600">
          <li>Vous cliquez sur « Activer les paiements en ligne »</li>
          <li>Stripe demande vos infos : société, RIB pro, pièce d'identité (10-15 min)</li>
          <li>Vous revenez sur cette page une fois validé</li>
          <li>Stripe vérifie votre dossier (1 min à 24 h)</li>
          <li>Une fois actif, le bouton « Payer en ligne » apparaît au checkout client</li>
        </ol>
        {t?.stripe_account_id && (
          <div className="border-t pt-2 text-xs text-slate-500">
            Stripe account ID : <code className="rounded bg-slate-100 px-1">{t.stripe_account_id}</code>
          </div>
        )}
      </div>
    </div>
  );
}
